import React, {
  lazy,
  Suspense,
  memo,
  useState,
  useEffect,
  useMemo
} from 'react';
import { withRouter } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import config from 'utils/config';
import analytics from '@xto10x/core/lib/analytics';
import storage from '@xto10x/storage/lib';
import {
  StorageKey,
  SidebarVisibilityPaths,
  DeepLinkPaths,
  UserViews,
  OkrModuleDataDependentPaths,
  AppConfigStorageKeys
} from 'utils/constants';
import {
  isStringEndsWith,
  getValueFromQueryParam
} from '@xto10x/core/lib/app.utils';
import { hasOKRModuleAccess } from 'utils/app.utils';
import { getModuleAccessCheckLib } from 'utils/accessControlUtils';

import componentLoader from 'utils/component.loader';
import moment from 'moment';
import {
  getFeatureConfig,
  getFeatureFlagConfig
} from 'store/AppConfig/reducer';
import { setAppConfig } from 'store/AppConfig/actions';
import useAppConfig from '@xto10x/config/lib';
import {
  getAccessibleModules,
  getUserRolesSelector
} from 'containers/App/store/reducer';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import {
  showAppLoading,
  hideAppLoading,
  resetSearchParamsAction,
  setCurrentUserViewDetails,
  fetchOKRModuleData,
  clearOKRHierarchyAlertMessage,
  fetchUserModules,
  fetchOrgSettings
} from '../store/actions';
import useAppInitializer from '../CustomHooks/AppInitializer';
import {
  getIsDropDownOpenSelector,
  getIsAuthenticatedSelector,
  getLoadingSelector,
  getAlertSelector,
  getCurrentUserInfoSelector,
  getDeviceDetailsSelector,
  getCurrentRTUEventStream,
  getIsNewAppVersionAvailableSelector,
  getNewVersionAvailableAlertSelector,
  getIsAnnualGoalsSpace,
  getIsAdvancedUser,
  getIsBasicUser,
  getNewAppRedirectionConfig,
  getFetchedOKRModuleData,
  getOrgAccessibleModules,
  getOKRHierarchyUpdatedAlertSelector,
  getObjectiveConfig,
  getIsCurrentUserManager,
  getCurrentUserHierarchyLevel,
  getIsCurrentUserHrbp,
  getIsRolesUpdatedSelector
} from '../store/reducer';
import {
  getObjectivesSelector,
  getFlatObjectivesSelector,
  getShowObjectiveLoader,
  getPaginationData
} from '../../Objective/store/reducer';
import {
  getSelectedTeamSelector,
  getSelectedObjectiveCycleSelector,
  getFilterParamsSelector,
  getSearchObjectiveParams
} from '../../Header/store/reducer';
import { getAllUsersMapSelector } from '../../Invite/store/reducer';
import {
  SidebarInvisibilityPaths,
  ToastActionType,
  QueryParam
} from '../../../utils/constants';
import { setUserRestrictions } from '../../Objective/store/actions';
import { currentUserRestrictions } from '../../../utils/objectives.utils';
import { useGetUserAttributes } from './hooks';
import { AppProvider } from '../App.context';
import { RoleAccessCheckLib } from '../../../utils/accessControlUtils';

const AppMobile = lazy(() => componentLoader(() => import('./App.mobile')));
const AppDesktop = lazy(() => componentLoader(() => import('./App.desktop')));

const App = memo(props => {
  const {
    accessibleModules,
    objectiveConfig,
    orgData,
    isRolesUpdated,
    currentUserInfo,
    isAuthenticated,
    fullScreenHandlers,
    searchObjectiveParams,
    isUserHrbp,
    isAdvancedUser,
    fetchedOKRModuleData,
    isBasicUser,
    okrHierarchyUpdatedAlert
  } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const appConfig = useAppConfig(AppConfigStorageKeys.objectStoreIdentifier);
  const [isSignedIn, setIsSignedIn] = useState(null);
  const [configInitialized, setConfigInitialized] = useState(false);
  const [httpClientConfig, setHttpClientConfig] = useState(null);

  const userRoles = useSelector(getUserRolesSelector);
  const isUserManager = useSelector(getIsCurrentUserManager);
  const currentUserHierarchyLevel = useSelector(getCurrentUserHierarchyLevel);

  const moduleAccessCheckLib = getModuleAccessCheckLib();

  const userDataAvailable = useMemo(
    () =>
      !!props.currentUserInfo?.user_reference_id &&
      !!props.orgData?.org_reference_id,
    [props.currentUserInfo?.user_reference_id, props.orgData?.org_reference_id]
  );
  const { data: userAttributes } = useGetUserAttributes(
    props.currentUserInfo?.user_reference_id,
    userDataAvailable
  );

  useEffect(() => {
    if (
      !objectiveConfig &&
      isSignedIn &&
      hasOKRModuleAccess(accessibleModules)
    ) {
      dispatch(fetchOrgSettings());
    } else {
      const restrictions = currentUserRestrictions(
        RoleAccessCheckLib.hasAdminOrSuperiorRoleInGoals(userRoles),
        isUserManager,
        objectiveConfig?.access_control
      );

      dispatch(setUserRestrictions(restrictions));
    }
  }, [
    userRoles,
    isSignedIn,
    isUserManager,
    objectiveConfig,
    accessibleModules,
    dispatch
  ]);

  useEffect(() => {
    const csd = getValueFromQueryParam(QueryParam.CUSTOM_DATE_RANGE_START_DATE);
    const ced = getValueFromQueryParam(QueryParam.CUSTOM_DATE_RANGE_END_DATE);
    if (
      window.location.pathname?.split('/')[2] === 'objectives' &&
      csd &&
      ced &&
      !(moment(+csd).isValid() && moment(+ced).isValid())
    ) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete(QueryParam.CUSTOM_DATE_RANGE_START_DATE);
      searchParams.delete(QueryParam.CUSTOM_DATE_RANGE_END_DATE);
      window.location.search = searchParams.toString();
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname.split('/').length > 2) {
      const isPeopleModule = ['survey', 'ama'].some(module =>
        window.location.pathname.split('/')[2].startsWith(module)
      );
      storage.saveDataInLocal(
        StorageKey.PRODUCT_ID,
        isPeopleModule ? 'people' : 'okr'
      );
      return;
    }
    storage.saveDataInLocal(StorageKey.PRODUCT_ID, 'okr');
  }, []);

  useEffect(() => {
    if (!appConfig || !orgData || configInitialized || !isRolesUpdated) return;
    dispatch(
      setAppConfig({
        [AppConfigStorageKeys.goals_feature_config]:
          appConfig?.[AppConfigStorageKeys.goals_feature_config] || null,
        [AppConfigStorageKeys.people_feature_config]:
          appConfig?.[AppConfigStorageKeys.people_feature_config] || null,
        [AppConfigStorageKeys.cues_feature_config]:
          appConfig?.[AppConfigStorageKeys.cues_feature_config] || null,
        [AppConfigStorageKeys.whats_new_config]:
          appConfig?.[AppConfigStorageKeys.whats_new_config] || null,
        userRoles
      })
    );
    setHttpClientConfig({
      goals: appConfig?.[AppConfigStorageKeys.goals_http_client_config] || null,
      people:
        appConfig?.[AppConfigStorageKeys.people_http_client_config] || null,
      reviews:
        appConfig?.[AppConfigStorageKeys.reviews_http_client_config] || null
    });
    if (
      appConfig[AppConfigStorageKeys.cues_feature_config]
        ?.analytics_tracking_config
    ) {
      analytics.updateTrackingOptions({
        ...appConfig[AppConfigStorageKeys.cues_feature_config]
          ?.analytics_tracking_config
      });
    }
    setConfigInitialized(true);
    storage.saveDataInLocal(
      StorageKey.CUSTOM_RANGE_VIEW_PRESET,
      appConfig?.[AppConfigStorageKeys.goals_feature_config]
        ?.custom_range_view_preset || null
    );
  }, [
    appConfig,
    orgData,
    configInitialized,
    userRoles,
    dispatch,
    isRolesUpdated
  ]);

  useEffect(() => {
    const pathSegment = location.pathname.split('/')[2];
    switch (pathSegment) {
      case 'surveys':
        if (httpClientConfig?.people) {
          storage.saveDataInLocal(
            StorageKey.HTTP_CLIENT_CONFIG,
            httpClientConfig.people
          );
        }
        break;
      case 'review':
        if (httpClientConfig?.reviews) {
          storage.saveDataInLocal(
            StorageKey.HTTP_CLIENT_CONFIG,
            httpClientConfig.reviews
          );
        }
        break;
      default:
        if (httpClientConfig?.goals) {
          storage.saveDataInLocal(
            StorageKey.HTTP_CLIENT_CONFIG,
            httpClientConfig.goals
          );
        }
        break;
    }
  }, [httpClientConfig, location.pathname]);

  useEffect(() => {
    if (currentUserInfo?.user_reference_id && orgData?.org_reference_id) {
      dispatch(fetchUserModules());
    }
  }, [orgData?.org_reference_id, dispatch, currentUserInfo?.user_reference_id]);

  const isCookieAndStorageAvailable = () =>
    storage.hasCookieAccess() && storage.hasLocalStorageAccess();
  const { isInitialized, hasCookieAndStorageAccess } = useAppInitializer({
    ...props,
    isSignedIn,
    setIsSignedIn
  });
  const [showSubHeader, setShowSubHeader] = useState(
    !!(
      window &&
      window.location &&
      window.location.pathname &&
      window.location.pathname.split('/')[2]?.startsWith('objectives') &&
      storage.getDataFromLocal(StorageKey.ORG_INFO)
    )
  );

  const [showSidebar, setShowSidebar] = useState(
    !!(
      window &&
      window.location &&
      window.location.pathname &&
      (isStringEndsWith(window.location.pathname, SidebarVisibilityPaths) ||
        (Object.values(DeepLinkPaths).some(word =>
          window.location.pathname.split('/')[2]?.startsWith(word)
        ) &&
          !SidebarInvisibilityPaths.some(path =>
            window.location.pathname.includes(path)
          ))) &&
      storage.getDataFromLocal(StorageKey.ORG_INFO)
    )
  );

  const orgInfoFromLocal = storage.getDataFromLocal(StorageKey.ORG_INFO);

  useEffect(() => {
    if (storage.getDataFromLocal(StorageKey.IS_PEOPLE_CUES_ORG)) return;
    document.title = orgInfoFromLocal?.name
      ? `PeopleCues - ${orgInfoFromLocal.name}`
      : 'PeopleCues';
  }, [orgInfoFromLocal]);

  useEffect(() => {
    if (!isInitialized) return;
    const orgInfo = storage.getDataFromLocal(StorageKey.ORG_INFO);
    if (storage.getDataFromLocal(StorageKey.IS_PEOPLE_CUES_ORG))
      document.title = orgInfo?.name
        ? `PeopleCues - ${orgInfo.name}`
        : 'PeopleCues';
  }, [isInitialized]);

  useEffect(() => {
    const org = storage.getDataFromLocal(StorageKey.ORG_INFO)?.sub_domain;
    if (
      location.pathname.split('/')[2]?.startsWith('objectives') &&
      isAuthenticated &&
      org
    ) {
      setShowSubHeader(true);
      return;
    }

    // reset search state in reducer
    if (
      !location.pathname.endsWith('/objectives') &&
      searchObjectiveParams &&
      searchObjectiveParams.searchActive
    ) {
      dispatch(resetSearchParamsAction());
    }
    // the header was getting an empty list.
    if (location.pathname === '/' && isAuthenticated && org) {
      window.history.pushState('', '', `/${org}/dashboard`);
    } else {
      setShowSubHeader(false);
    }
  }, [
    isAuthenticated,
    location.pathname,
    fullScreenHandlers,
    dispatch,
    searchObjectiveParams
  ]);

  useEffect(() => {
    if (
      isAuthenticated &&
      (isStringEndsWith(location.pathname, SidebarVisibilityPaths) ||
        (Object.values(DeepLinkPaths).some(word =>
          location.pathname.split('/')[2]?.startsWith(word)
        ) &&
          !SidebarInvisibilityPaths.some(path =>
            location.pathname.includes(path)
          )) ||
        isStringEndsWith(location.pathname, [
          currentUserInfo?.user_reference_id
        ])) &&
      isInitialized
    ) {
      setShowSidebar(true);
    } else setShowSidebar(false);
  }, [
    isAuthenticated,
    location.pathname,
    isInitialized,
    fullScreenHandlers,
    currentUserInfo
  ]);

  useEffect(() => {
    if (
      isAuthenticated &&
      orgData?.org_reference_id &&
      currentUserInfo?.user_reference_id &&
      accessibleModules &&
      accessibleModules.length > 0 &&
      window.pendo &&
      !!userAttributes
    ) {
      const visitorData = {
        id: `${orgData.name}/${currentUserInfo.name}`,
        user_id: currentUserInfo.user_reference_id,
        user_name: currentUserInfo.name,
        user_email: currentUserInfo.email,
        user_role: currentUserInfo.role,
        roles: JSON.stringify(userRoles),
        people_role: userRoles.people,
        apps_role: userRoles.apps,
        okr_role: userRoles.okr,
        perf_role: userRoles.perf,
        is_manager: isUserManager,
        is_hrbp: isUserHrbp,
        user_level: currentUserHierarchyLevel,
        pod: userAttributes?.pod,
        functional_leader: userAttributes?.functional_leader,
        has_review_module: moduleAccessCheckLib.hasReviewModuleAccess(
          accessibleModules
        ),
        has_objective_module: moduleAccessCheckLib.hasOkrModuleAccess(
          accessibleModules
        ),
        has_survey_module: moduleAccessCheckLib.hasSurveyModuleAccess(
          accessibleModules
        ),
        has_feedback_module: moduleAccessCheckLib.hasFeedbackModuleAccess(
          accessibleModules
        ),
        has_ama_module: moduleAccessCheckLib.hasAmaModuleAccess(
          accessibleModules
        ),
        has_advanced_okr: moduleAccessCheckLib.hasAdvancedOkrAccess(
          accessibleModules
        ),
        has_basic_okr: moduleAccessCheckLib.hasBasicOkrAccess(accessibleModules)
      };
      /* https://support.pendo.io/hc/en-us/articles/360031862272-Installation-for-Single-Page-Frameworks#validation-and-troubleshooting-0-11 */
      Object.keys(visitorData).forEach(key => {
        if (visitorData[key] !== false && !visitorData[key])
          delete visitorData[key];
      });
      window.pendo.initialize({
        visitor: visitorData,
        account: {
          id: orgData.name,
          org_id: orgData.org_reference_id,
          org_status: orgData.org_status
        }
      });
    }
  }, [
    isAuthenticated,
    orgData,
    currentUserInfo,
    accessibleModules,
    isUserHrbp,
    userRoles,
    isUserManager,
    currentUserHierarchyLevel,
    moduleAccessCheckLib,
    userAttributes
  ]);

  useEffect(() => {
    if (isInitialized) dispatch(hideAppLoading());
    else dispatch(showAppLoading());
    analytics.setCustomDimension('dimension4', config.appVersion);
    const storedOrgInfo = storage.getDataFromLocal(StorageKey.ORG_INFO);
    if (storedOrgInfo) {
      analytics.setCustomDimension('dimension2', storedOrgInfo.name);
      analytics.setCustomDimension('dimension3', storedOrgInfo.sub_domain);
    }
  }, [isInitialized, dispatch]);

  useEffect(() => {
    if (isAdvancedUser === null || isBasicUser === null) return;
    if (isAdvancedUser && isBasicUser)
      dispatch(setCurrentUserViewDetails(UserViews.ADVANCED_BASIC));
    else if (isAdvancedUser)
      dispatch(setCurrentUserViewDetails(UserViews.ADVANCED));
    else if (isBasicUser) dispatch(setCurrentUserViewDetails(UserViews.BASIC));
  }, [isAdvancedUser, isBasicUser, dispatch]);

  useEffect(() => {
    if (
      isSignedIn !== true ||
      !storage.getDataFromLocal(StorageKey.ORG_INFO) ||
      !storage.getDataFromLocal(StorageKey.USER_INFO) ||
      !accessibleModules ||
      accessibleModules.length === 0
    )
      return;
    if (!location?.pathname || fetchedOKRModuleData !== null) return;
    if (
      location.pathname.split('/').length > 2 &&
      OkrModuleDataDependentPaths.indexOf(location.pathname.split('/')[2]) >
        -1 &&
      hasOKRModuleAccess(accessibleModules)
    )
      dispatch(fetchOKRModuleData(false));
  }, [
    isSignedIn,
    accessibleModules,
    location?.pathname,
    fetchedOKRModuleData,
    dispatch
  ]);

  useEffect(() => {
    if (
      okrHierarchyUpdatedAlert &&
      okrHierarchyUpdatedAlert?.alertAction?.actionType ===
        ToastActionType.OKR_HEIRARCHY_UPDATED
    )
      dispatch(clearOKRHierarchyAlertMessage());
  }, [location?.search, dispatch, okrHierarchyUpdatedAlert]);

  useEffect(() => {
    if (fullScreenHandlers.objectives?.active) {
      setShowSidebar(false);
      setShowSubHeader(false);
    }
  }, [fullScreenHandlers]);

  const getComponent = () => {
    if (props.deviceDetails.mobile)
      return (
        <AppMobile
          {...props}
          isInitialized={isInitialized}
          showSidebar={showSidebar}
          showSubHeader={showSubHeader}
          hasCookieAndStorageAccess={hasCookieAndStorageAccess}
          isCookieAndStorageAvailable={isCookieAndStorageAvailable}
          currentUserInfo={props.currentUserInfo}
          allUsersMap={props.allUsersMap}
          userRoles={userRoles}
        />
      );
    return (
      <AppDesktop
        {...props}
        isInitialized={isInitialized}
        showSidebar={showSidebar}
        showSubHeader={showSubHeader}
        hasCookieAndStorageAccess={hasCookieAndStorageAccess}
        isCookieAndStorageAvailable={isCookieAndStorageAvailable}
        currentUserInfo={props.currentUserInfo}
        allUsersMap={props.allUsersMap}
        fullScreenHandlers={props.fullScreenHandlers}
        objectiveConfig={props.objectiveConfig}
        userRoles={userRoles}
      />
    );
  };

  return (
    <Suspense
      fallback={
        <div className="loaderContainer">
          <div className="loaderWrapper">
            <div className="loader">Loading...</div>
          </div>
        </div>
      }>
      <AppProvider isAuthenticated={userDataAvailable}>
        {getComponent()}
      </AppProvider>
    </Suspense>
  );
});

const mapStateToProps = state => ({
  isDropDownOpen: getIsDropDownOpenSelector(state),
  isAuthenticated: getIsAuthenticatedSelector(state),
  deviceDetails: getDeviceDetailsSelector(state),
  loading: getLoadingSelector(state),
  alert: getAlertSelector(state),
  currentUserInfo: getCurrentUserInfoSelector(state),
  objectives: getObjectivesSelector(state),
  flatObjectives: getFlatObjectivesSelector(state),
  selectedTeam: getSelectedTeamSelector(state),
  selectedObjectiveCycle: getSelectedObjectiveCycleSelector(state),
  filters: getFilterParamsSelector(state),
  diffOrgData: state.appReducer.diffOrgData,
  userTeamsData: state.appReducer.userTeamsData,
  searchObjectiveParams: getSearchObjectiveParams(state),
  showCompanyObjectivesOnly: state.headerReducer.showCompanyObjectivesOnly,
  RTUEventStream: getCurrentRTUEventStream(state),
  isNewAppVersionAvailable: getIsNewAppVersionAvailableSelector(state),
  newVersionAvailableAlert: getNewVersionAvailableAlertSelector(state),
  okrHierarchyUpdatedAlert: getOKRHierarchyUpdatedAlertSelector(state),
  orgData: state.appReducer.orgData,
  isAnnualGoalsSpace: getIsAnnualGoalsSpace(state),
  featureConfig: getFeatureConfig(state),
  featureFlagConfig: getFeatureFlagConfig(state),
  isAdvancedUser: getIsAdvancedUser(state),
  isBasicUser: getIsBasicUser(state),
  newAppRedirectionConfig: getNewAppRedirectionConfig(state),
  accessibleModules: getAccessibleModules(state),
  orgAccessibleModules: getOrgAccessibleModules(state),
  allUsersMap: getAllUsersMapSelector(state),
  fetchedOKRModuleData: getFetchedOKRModuleData(state),
  fullScreenHandlers: state.appReducer.fullscreenHandlers,
  objectiveConfig: getObjectiveConfig(state),
  showObjectiveLoader: getShowObjectiveLoader(state),
  paginationData: getPaginationData(state),
  isUserHrbp: getIsCurrentUserHrbp(state),
  isRolesUpdated: getIsRolesUpdatedSelector(state)
});

export default withRouter(connect(mapStateToProps)(App));
